/**
 * iOS Add-to-Home-Screen guidance (task.md §4.14: push only works when installed). Shown in Safari on
 * iPhone/iPad while the page is not running standalone; dismissed once per device (localStorage).
 */
export const INSTALL_GUIDE_DISMISSED_KEY = 'agrogruda_ios_install_dismissed';

export interface InstallGuideEnv {
  userAgent: string;
  /** Safari's non-standard navigator.standalone (true inside the installed app). */
  standalone: boolean | undefined;
  /** matchMedia('(display-mode: standalone)').matches */
  displayModeStandalone: boolean;
}

export function isIos(userAgent: string): boolean {
  return /iPhone|iPad|iPod/i.test(userAgent) || (/Macintosh/i.test(userAgent) && /Mobile/i.test(userAgent));
}

/** Safari itself, not an in-app browser or Chrome/Firefox for iOS (they cannot install web apps). */
export function isIosSafari(userAgent: string): boolean {
  return (
    isIos(userAgent) &&
    /Safari/i.test(userAgent) &&
    !/CriOS|FxiOS|EdgiOS|OPiOS|FBAN|FBAV|Instagram/i.test(userAgent)
  );
}

export function needsIosInstallGuide(env: InstallGuideEnv): boolean {
  if (!isIosSafari(env.userAgent)) return false;
  if (env.standalone === true || env.displayModeStandalone) return false;
  return true;
}

export function currentInstallGuideEnv(): InstallGuideEnv {
  const nav = navigator as Navigator & { standalone?: boolean };
  return {
    userAgent: nav.userAgent,
    standalone: nav.standalone,
    displayModeStandalone:
      typeof window.matchMedia === 'function' && window.matchMedia('(display-mode: standalone)').matches,
  };
}

export function installGuideDismissed(storage: Pick<Storage, 'getItem'> | null = safeStorage()): boolean {
  try {
    return storage?.getItem(INSTALL_GUIDE_DISMISSED_KEY) === '1';
  } catch {
    return false;
  }
}

export function dismissInstallGuide(storage: Pick<Storage, 'setItem'> | null = safeStorage()): void {
  try {
    storage?.setItem(INSTALL_GUIDE_DISMISSED_KEY, '1');
  } catch {
    // storage unavailable: the guide simply shows again next time
  }
}

function safeStorage(): Storage | null {
  try {
    return typeof localStorage === 'undefined' ? null : localStorage;
  } catch {
    return null;
  }
}
